import React, { useState, useCallback, useRef, useMemo } from "react";
import { navigate } from "gatsby-link";
import FormSelect from "./elements/form-select";
import FormInput from "./elements/form-input";
import FormAutocomplete from "./elements/form-autocomplete";
import FormPrivacy from "./elements/form-privacy";
import { validateCode, validateEmail } from "../utility/formatters";
import { getPrivacyData, submitData } from "../utility/forms";
import { GroupProvider } from "../context/group";
import useBabelModels from "../hooks/use-babel-models";
import AltchaWidget from "../utility/altcha";

const initialState = {
  type: "User manual request",
  productCategory: "",
  model: "",
  serialNumber: "",
  firstName: "",
  lastName: "",
  email: "",
};

const Form4 = ({ data, labelManagerLabels, location }) => {
  const [formData, setFormData] = useState(() => {
    let baseData;

    if (location.search) {
      const urlSearchparams = new URLSearchParams(location.search);
      const lineId = urlSearchparams.get("lineId");
      const matchItem = data.lines.selectGroups.find(
        (item) =>
          (item.label && item.label.startsWith(lineId)) ||
          (item.group && item.group.find((sub) => sub.value.startsWith(lineId)))
      );
      baseData = {
        productCategory: matchItem
          ? matchItem.label?.startsWith(lineId)
            ? matchItem.label
            : matchItem.group.map((sub) => sub.value).find((val) => val.startsWith(lineId))
          : "",
        model: urlSearchparams.get("model") || "",
        serialNumber: urlSearchparams.get("productId") || "",
      };
    }
    return {
      ...initialState,
      ...baseData,
      ...getPrivacyData(data),
    };
  });
  const lineOpts = useMemo(
    () =>
      [
        {
          value: "",
          label: labelManagerLabels.select,
        },
      ].concat(data.lines.selectGroups),
    [data, labelManagerLabels]
  );

  const modelli = useBabelModels(formData.productCategory.split("_")[0]);

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const altchaRef = useRef();

  const onChange = useCallback((e) => {
    const { name } = e.target;
    setFormData((current) => {
      const { name, value, type, checked } = e.target;
      let serialNumber, model;
      if (name === "productCategory" && value !== current.productCategory) {
        serialNumber = "";
        model = "";
      }
      /* const formatter = changeFormatters[name]; */
      const newValue = type === "checkbox" ? checked : value;
      return {
        ...current,
        [name]: newValue,
        ...(model !== undefined && { model }),
        ...(serialNumber !== undefined && { serialNumber }),
      };
    });
    setFormErrors((current) => (current[name] ? { ...current, [name]: false } : current));
  }, []);

  const formDataRef = useRef({});
  formDataRef.current = formData;

  const formInfo = useMemo(
    () => ({
      formData,
      onChange,
      formErrors,
      setFormErrors,
    }),
    [formData, onChange, formErrors, setFormErrors]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.currentTarget;
      const formElements = [...form.elements];
      const missingFields = formElements.filter(
        (field) =>
          field.getAttribute &&
          field.getAttribute("data-required") === "true" &&
          (!field.value || (field.type === "checkbox" && !field.checked))
      );

      const invalidFields = [];
      if (form.serialNumber?.value && !validateCode(form.serialNumber?.value)) {
        invalidFields.push("serialNumber");
      }
      if (form.email?.value && !validateEmail(form.email.value)) {
        invalidFields.push("email");
      }
      /* if (form.phone.value && !validateTel(form.phone.value)) {
      invalidFields.push('phone');
    }
    if (form.mobilePhone.value && !validateTel(form.mobilePhone.value)) {
      invalidFields.push('mobilePhone');
    } */

      if (missingFields.length || invalidFields.length) {
        const missingErrs = missingFields.reduce(
          (res, field) => ({
            ...res,
            [field.name]: labelManagerLabels.requiredFields,
          }),
          {}
        );
        const errors = invalidFields.reduce(
          (res, field) => ({ ...res, [field]: true }),
          missingErrs
        );
        setFormErrors((current) => ({ ...current, ...errors }));
        console.warn("Cannot submit: ", errors);
        const firstElError = formElements.find(
          (el) =>
            missingFields.some((field) => el.name === field.name) || invalidFields.includes(el.name)
        );

        var headerOffset =
          document.getElementsByClassName("header")[0].getBoundingClientRect().height + 50;
        var elementPosition = firstElError.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          behavior: "smooth",
          top: offsetPosition,
        });

        return;
      }

      setLoading(true);
      setServerError(false);

      const formPayload = {
        ...formDataRef.current,
        model: modelli.find((item) => item.productName === formDataRef.current.model)?.modelName,
      };

      try {
        const result = await submitData({ ...formPayload }, captcha, data.privacyText, "form4");
        console.log("result: ", result);
        if (data.retUrl) {
          const slug = data.retUrl;
          navigate(slug.startsWith("/") ? slug : `/${slug}`);
        } else {
          navigate("/");
        }
      } catch (err) {
        setServerError(true);
        console.error("Error submitting: ", err);
        altchaRef.current?.reset();
        setCaptcha(null);
      }

      setLoading(false);
    },
    [data.privacyText, data.retUrl, labelManagerLabels, modelli, captcha]
  );

  // console.log(modelli)
  return (
    <section className="section form">
      <div className="container">
        <div className="row">
          <div className="col-12 px-0">
            <div className="form__container">
              <form className="form" onSubmit={onSubmit}>
                <GroupProvider data={formInfo}>
                  <FormSelect
                    id="line"
                    field="productCategory"
                    options={lineOpts}
                    label={labelManagerLabels.formProductLine}
                    isRequired={true}
                  />
                  {formData.productCategory &&
                    (!modelli ? (
                      <div className="py-4 d-flex align-items-center justify-content-center">
                        <div className="spinner-border"></div>
                      </div>
                    ) : (
                      <>
                        <FormAutocomplete
                          id="productModel"
                          field="model"
                          maxLength={20}
                          options={modelli ? modelli.map((item) => item.productName) : []}
                          isRequired={true}
                          label={labelManagerLabels.formModel}
                          autoSelect={false}
                        />
                        <FormInput
                          id="productCode"
                          field="serialNumber"
                          maxLength={20}
                          label={`${
                            labelManagerLabels.formProductCode || labelManagerLabels.serialNumber
                          } *`}
                          isRequired={true}
                          validate={validateCode}
                          formatWarning={labelManagerLabels.serialNumberValidationHaier}
                        />
                      </>
                    ))}
                  <FormInput
                    id="firstName"
                    field="firstName"
                    maxLength={40}
                    placeholder={`${labelManagerLabels.formName} *`}
                    isRequired={true}
                  />
                  <FormInput
                    id="lastName"
                    field="lastName"
                    maxLength={80}
                    placeholder={`${labelManagerLabels.formLastName} *`}
                    isRequired={true}
                  />
                  <FormInput
                    id="email"
                    field="email"
                    type="email"
                    maxLength={80}
                    placeholder={`${labelManagerLabels.formEmail} *`}
                    isRequired={true}
                    validate={validateEmail}
                    formatWarning={labelManagerLabels.validEmail}
                  />

                  <FormPrivacy data={data.privacyText} labelManagerLabels={labelManagerLabels} />

                  <div className="my-5">
                    <AltchaWidget ref={altchaRef} onChange={setCaptcha} />
                  </div>
                  <div className="text-center">
                    <button className="btn-link button primary" disabled={!captcha || loading}>
                      {labelManagerLabels.send}
                    </button>
                    {serverError && (
                      <p className="mt-2 form__service-not-available">
                        {labelManagerLabels.serviceNotAvailable}
                      </p>
                    )}
                  </div>

                  <div className="form__info mt-4">* {labelManagerLabels.requiredFields}</div>
                </GroupProvider>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form4;
